import React, { useState, useRef, useEffect } from 'react';

interface SearchableSelectOption {
  value: string | number;
  label: string;
  searchText?: string;
}

interface SearchableSelectProps {
  options: SearchableSelectOption[];
  value: string | number;
  onChange: (value: string | number) => void;
  placeholder?: string;
  disabled?: boolean;
  hasError?: boolean;
  emptyMessage?: string;
  // Server-side mode: when `onSearchChange` is provided the parent owns
  // filtering/pagination, so client-side filtering is skipped, the search term
  // is preserved across opens, and `onScrollEnd` fires for infinite scroll.
  onSearchChange?: (term: string) => void;
  onScrollEnd?: () => void;
  onOpen?: () => void;
  isLoading?: boolean;
}

const SearchableSelect: React.FC<SearchableSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = '-- Seleccionar --',
  disabled = false,
  hasError = false,
  emptyMessage = 'No hay opciones disponibles',
  onSearchChange,
  onScrollEnd,
  onOpen,
  isLoading = false
}) => {
  const serverSide = !!onSearchChange;
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find(opt => opt.value === value);

  // In server-side mode the parent already returns the matching options, so we
  // render them as-is; otherwise filter locally by the search term.
  const filteredOptions = serverSide
    ? options
    : options.filter(option => {
        const searchableText = option.searchText || option.label;
        return searchableText.toLowerCase().includes(searchTerm.toLowerCase());
      });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        if (!serverSide) setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && highlightedIndex >= 0 && dropdownRef.current) {
      const highlightedElement = dropdownRef.current.children[highlightedIndex] as HTMLElement;
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [highlightedIndex, isOpen]);

  // Keep the search term across opens in server-side mode so the visible list
  // stays consistent with the parent's last query; clear it otherwise.
  const clearSearch = () => {
    if (!serverSide) setSearchTerm('');
  };

  const openDropdown = () => {
    setIsOpen(true);
    setHighlightedIndex(-1);
    onOpen?.();
  };

  const closeDropdown = () => {
    setIsOpen(false);
    clearSearch();
    setHighlightedIndex(-1);
  };

  const handleToggle = () => {
    if (disabled) return;
    if (isOpen) {
      closeDropdown();
    } else {
      openDropdown();
    }
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (!onScrollEnd) return;
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 48) {
      onScrollEnd();
    }
  };

  const handleOptionClick = (optionValue: string | number) => {
    onChange(optionValue);
    closeDropdown();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!isOpen) {
          openDropdown();
        } else {
          setHighlightedIndex(prev => 
            prev < filteredOptions.length - 1 ? prev + 1 : prev
          );
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (isOpen) {
          setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0);
        }
        break;
      case 'Enter':
        e.preventDefault();
        if (isOpen && highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
          handleOptionClick(filteredOptions[highlightedIndex].value);
        } else if (!isOpen) {
          openDropdown();
        }
        break;
      case 'Escape':
        e.preventDefault();
        closeDropdown();
        break;
      case 'Tab':
        if (isOpen) {
          setIsOpen(false);
          clearSearch();
        }
        break;
    }
  };

  const borderColor = hasError ? '#fbbf24' : '#d1d5db';
  const borderWidth = hasError ? '2px' : '1px';
  const caretColor = hasError ? '#fbbf24' : '#374151';

  return (
    <div 
      ref={containerRef}
      style={{ 
        position: 'relative',
        width: '100%'
      }}
      onKeyDown={handleKeyDown}
    >
      <div
        onClick={handleToggle}
        style={{
          width: '100%',
          padding: '10px 40px 10px 12px',
          border: `${borderWidth} solid ${borderColor}`,
          borderRadius: 6,
          fontSize: 14,
          backgroundColor: disabled ? '#f9fafb' : '#fff',
          color: value ? '#1f2937' : '#9ca3af',
          cursor: disabled ? 'not-allowed' : 'pointer',
          outline: 'none',
          boxSizing: 'border-box',
          userSelect: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'relative'
        }}
        tabIndex={disabled ? -1 : 0}
      >
        <span style={{ 
          overflow: 'hidden', 
          textOverflow: 'ellipsis', 
          whiteSpace: 'nowrap',
          flex: 1
        }}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          width="12" 
          height="12" 
          viewBox="0 0 12 12"
          style={{
            position: 'absolute',
            right: 14,
            top: '50%',
            transform: isOpen ? 'translateY(-50%) rotate(180deg)' : 'translateY(-50%)',
            transition: 'transform 0.2s ease',
            pointerEvents: 'none'
          }}
        >
          <path fill={caretColor} d="M6 9L1 4h10z" />
        </svg>
      </div>

      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            backgroundColor: '#fff',
            border: '1px solid #d1d5db',
            borderRadius: 6,
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            zIndex: 1000,
            maxHeight: 280,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}
        >
          <div style={{ 
            padding: '8px',
            borderBottom: '1px solid #e5e7eb',
            position: 'sticky',
            top: 0,
            backgroundColor: '#fff',
            zIndex: 1
          }}>
            <input
              ref={searchInputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setHighlightedIndex(0);
                onSearchChange?.(e.target.value);
              }}
              placeholder="Buscar..."
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: 4,
                fontSize: 13,
                outline: 'none',
                boxSizing: 'border-box'
              }}
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          <div
            ref={dropdownRef}
            onScroll={handleScroll}
            style={{
              overflowY: 'auto',
              maxHeight: 224
            }}
          >
            {filteredOptions.length === 0 && !isLoading ? (
              <div style={{
                padding: '12px',
                fontSize: 13,
                color: '#6b7280',
                textAlign: 'center'
              }}>
                {searchTerm ? `No se encontraron resultados para "${searchTerm}"` : emptyMessage}
              </div>
            ) : (
              filteredOptions.map((option, index) => (
                <div
                  key={option.value}
                  onClick={() => handleOptionClick(option.value)}
                  style={{
                    padding: '10px 12px',
                    fontSize: 14,
                    cursor: 'pointer',
                    backgroundColor: 
                      option.value === value ? '#e0f2f1' : 
                      index === highlightedIndex ? '#f3f4f6' : 
                      '#fff',
                    color: '#1f2937',
                    transition: 'background-color 0.1s ease',
                    borderLeft: option.value === value ? '3px solid #38b2ac' : '3px solid transparent'
                  }}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  {option.label}
                </div>
              ))
            )}
            {isLoading && (
              <div style={{
                padding: '10px 12px',
                fontSize: 13,
                color: '#6b7280',
                textAlign: 'center'
              }}>
                Cargando...
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
